import React, { useState, useEffect, useRef, FC, useCallback, useContext } from 'react';
import "./InstanceDropdownComp.scss";
import { PropsType, LicenseInstance } from './InstanceDropdownCompTypes';
import ArrowDownLineIcon from '../../../Images/ArrowDownLineIcon';
import ArrowUpLineIcon from '../../../Images/ArrowUpLineIcon';
import {invokeKeyboard, closeKeyboard} from "../../KeyboardUtils/KeyboardHookUtils";
import { AppContext } from "../../../Context/Context";

export const InstanceDropdownComp: FC<PropsType> = ({
    licenseInstances,
    selectedInstanceKey,
    appType,
    onInstanceSelect
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const {setKeyboardVisibility}: any = useContext(AppContext);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
                setSearchTerm("");
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const onMessageReceivedFromChild = useCallback((e: any) => {
        const activeElement = document.activeElement;
        if (activeElement && activeElement.className === "custom-dropdown-search-inline") {
            if (typeof e.data === "string") {
                setSearchTerm(e.data);
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener("message", onMessageReceivedFromChild);
        return () => {
            window.removeEventListener("message", onMessageReceivedFromChild);
        };
    }, [onMessageReceivedFromChild]);

    const handleInstanceSelect = (instance: LicenseInstance) => {
        setIsDropdownOpen(false);
        setSearchTerm("");
        onInstanceSelect(instance.RegisterationKey);
        // Keep keyboard visible after selection
        setKeyboardVisibility(true);
        setTimeout(() => invokeKeyboard(null, ""), 100);
    };

    const getSelectedInstanceLabel = () => {
        if (searchTerm) {
            return searchTerm;
        }
        const selected = licenseInstances.find(inst => inst.RegisterationKey === selectedInstanceKey);
        if (selected) {
            return (
                <span>
                    <span style={{ color: '#000', fontWeight: 'bold' }}>{'{'}</span>
                    {selected.InstanceName}
                    <span style={{ color: '#000', fontWeight: 'bold' }}>{'}'}</span>
                    {' '}
                    <span style={{ color: '#000', fontWeight: 'bold' }}>{'{'}</span>
                    {selected.LicenseProfileName}
                    <span style={{ color: '#000', fontWeight: 'bold' }}>{'}'}</span>
                    {' '}
                    <span style={{ color: '#000', fontWeight: 'bold' }}>{'{'}</span>
                    {selected.LicenseUsageName}
                    <span style={{ color: '#000', fontWeight: 'bold' }}>{'}'}</span>
                    {
                        appType === "Mothership" || appType === "AppCentral" ? "" : <>
                            {' ('}{selected.UsedLicenseCount}/{selected.LicenseCount}{')'}
                        </>
                    }
                </span>
            );
        }
        return "Select An Instance";
    };

    const filteredInstances = licenseInstances.filter((instance) => {
        const searchStr = searchTerm.toLowerCase();
        const fullDisplayText = `{${instance.InstanceName}} {${instance.LicenseProfileName}} {${instance.LicenseUsageName}} ${appType === "Mothership" || appType === "AppCentral" ? "" : `(${instance.UsedLicenseCount}/${instance.LicenseCount})`} `.toLowerCase();
        const searchableText = fullDisplayText.replace(/[{}]/g, ' ').replace(/\s+/g, ' ').trim();

        return (
            searchableText.includes(searchStr) ||
            fullDisplayText.includes(searchStr) ||
            instance.InstanceName?.toLowerCase().includes(searchStr) ||
            instance.LicenseProfileName?.toLowerCase().includes(searchStr) ||
            instance.LicenseUsageName?.toLowerCase().includes(searchStr)
        );
    });


    if (!licenseInstances || licenseInstances.length === 0) {
        return null;
    }

    return (
        <div className="instance-dropdown-container">
            <div className="instance-dropdown-label">
                Instance
            </div>
            <div className="custom-dropdown" ref={dropdownRef}>
                {!isDropdownOpen ? (
                    <div
                        className="custom-dropdown-toggle"
                        onClick={() => {
                            setIsDropdownOpen(true);
                            setKeyboardVisibility(true);
                            setTimeout(() => invokeKeyboard(null, searchTerm), 100);
                        }}
                    >
                        <span className="custom-dropdown-text">{getSelectedInstanceLabel()}</span>
                        <span className="custom-dropdown-arrow">
                            <ArrowDownLineIcon style={{ fontSize: '18px' }} />
                        </span>
                    </div>
                ) : (
                    <div className="custom-dropdown-toggle search-active">
                        <input
                            type="text"
                            className="custom-dropdown-search-inline"
                            placeholder="search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={false}
                        />
                        <span
                            className="custom-dropdown-arrow open"
                            onClick={() => {
                                setIsDropdownOpen(false);
                                setSearchTerm("");
                            }}
                        >
                            <ArrowUpLineIcon style={{ fontSize: '18px' }} />
                        </span>
                    </div>
                )}
                {isDropdownOpen && (
                    <div className="custom-dropdown-menu">
                        <div className="custom-dropdown-options">
                            {filteredInstances.length > 0 ? (
                                filteredInstances.map((instance, index) => (
                                    <div
                                        key={index}
                                            className={`custom-dropdown-option ${selectedInstanceKey === instance.RegisterationKey ? 'selected' : ''}`}
                                        onClick={() => handleInstanceSelect(instance)}
                                    >
                                        <span>
                                            <span style={{ color: '#000', fontWeight: 'bold' }}>{'{'}</span>
                                            {instance.InstanceName}
                                            <span style={{ color: '#000', fontWeight: 'bold' }}>{'}'}</span>
                                            {' '}
                                            <span style={{ color: '#000', fontWeight: 'bold' }}>{'{'}</span>
                                            {instance.LicenseProfileName}
                                            <span style={{ color: '#000', fontWeight: 'bold' }}>{'}'}</span>
                                            {' '}
                                            <span style={{ color: '#000', fontWeight: 'bold' }}>{'{'}</span>
                                            {instance.LicenseUsageName}
                                            <span style={{ color: '#000', fontWeight: 'bold' }}>{'}'}</span>
                                            {
                                                appType === "Mothership" || appType === "AppCentral" ? "" : <>
                                                    {' ('}{instance.UsedLicenseCount}/{instance.LicenseCount}{')'}
                                                </>
                                            }
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="custom-dropdown-option disabled">No results found</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(InstanceDropdownComp);
